<!-- @format -->

<template>
	<div class="login" :style="{ background: oemDetail.loginBackgroundImageUrl, backgroundSize: 'cover' }">
		<el-form ref="loginRef" :model="loginForm" :rules="loginRules" hide-required-asterisk class="login-form">
			<div class="logo">
				<img :src="oemDetail.loginLogoUrl" alt="" class="loginLogo" />
				<div class="logo-divider" />
				<div
					class="loginName"
					v-html="getFrontEndConfigItemByCodeAndItemCode(globalStore.frontEndConfig, 'login', 'title')?.itemValue"
				/>
			</div>
			<div v-if="isExpired" class="empower">
				<span>您好，当前平台License授权许可证书异常，请联系管理员更新授权。</span>
			</div>
			<div v-else>
				<el-form-item prop="userAccount" label="用户名">
					<el-input
						v-model="loginForm.userAccount"
						type="primary"
						link
						size="large"
						auto-complete="off"
						placeholder="请输入用户名称"
					>
					</el-input>
				</el-form-item>
				<el-form-item prop="password" label="密码">
					<el-input
						v-model="loginForm.password"
						type="password"
						size="large"
						auto-complete="off"
						placeholder="请输入密码"
						show-password
					>
					</el-input>
				</el-form-item>
				<el-form-item prop="code" label="验证码" class="form-code">
					<div class="code-main">
						<el-input
							v-model="loginForm.code"
							size="large"
							auto-complete="off"
							placeholder="请输入验证码"
							@keyup.enter="handleLogin"
						>
						</el-input>
						<div class="code-image">
							<img
								style="width: 100%; height: 100%"
								:src="captchaImg"
								alt="验证码"
								class="captcha-img"
								title="点击刷新"
								@click="refreshCode"
							/>
						</div>
					</div>
				</el-form-item>
			</div>
			<el-button
				v-if="!isExpired"
				class="loginButton"
				:loading="loading"
				size="large"
				type="primary"
				style="width: 100%"
				@click.prevent="handleLogin"
			>
				{{ loading ? '登 录 中...' : '登 录' }}
			</el-button>
			<div v-if="register" style="float: right">
				<router-link class="link-type" :to="'/register'">立即注册</router-link>
			</div>
		</el-form>
		<!--  底部  -->
		<div class="el-login-footer">
			<span
				v-html="getFrontEndConfigItemByCodeAndItemCode(globalStore.frontEndConfig, 'login', 'copyright')?.itemValue"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import {
		useGlobalStoreWithOut,
		getFileUrlById,
		getFrontEndConfigItemByCodeAndItemCode,
		getEnvUrlByUrl,
	} from '@power/runtime-vue3';
	import { ElMessage } from 'element-plus';
	import { getCaptchaApi, verifyCaptchaApi } from '@/api/login/index';
	import { getPublicKey } from '@/api/login';
	import { encodeStr } from '@/utils/encrypt';

	// const router = useRouter();
	const { proxy } = getCurrentInstance() as any;
	const globalStore = useGlobalStoreWithOut();

	const isExpired = ref<any>(false);

	const oemDetail = ref({
		loginBackgroundImageUrl: '',
		loginLogoUrl: '',
	});

	const loginForm = ref({
		userAccount: '',
		password: '',
		code: '',
		uuid: '',
	});

	const loginRules = {
		userAccount: [{ required: true, trigger: 'blur', message: '请输入您的用户名称' }],
		password: [{ required: true, trigger: 'blur', message: '请输入您的密码' }],
		code: [
			{ required: true, trigger: 'blur', message: '请输入验证码' },
			{ min: 4, max: 4, message: '验证码长度为4位', trigger: 'blur' },
		],
	};

	const loading = ref(false);
	// 注册开关
	const register = ref(false);

	const showCodeTimer = ref<any>(null);

	const captchaImg = ref('');

	const captchaUuid = ref('');

	const getCaptchaImage = async () => {
		try {
			const res: any = await getCaptchaApi();
			if (res.code === 200) {
				captchaImg.value = `data:image/gif;base64,${res.data.imgBase64}`;
				captchaUuid.value = res.data.uuid;
			} else {
				ElMessage.error('获取验证码失败，请重试');
			}
		} catch (error) {}
	};

	function handleLogin() {
		proxy.$refs.loginRef.validate(async (valid) => {
			if (valid) {
				loading.value = true;
				try {
					const res: any = await verifyCaptchaApi({
						code: loginForm.value.code,
						uuid: captchaUuid.value,
					});
					if (res.data) {
						const authType = globalStore.authInfo.authType;
						const res: any = await getPublicKey();
						globalStore
							.login(authType, {
								userAccount: loginForm.value.userAccount,
								password: await encodeStr(loginForm.value.password, res.data.publicKey),
								keyId: res.data.keyId,
							})
							.then((res) => {
								//console.log('login.success, should push', res);
								if (res.refreshedRoute) {
									//console.log('已跳转到指定地址');
								} else {
									// router.push({ path: '/' });
									location.href = getEnvUrlByUrl('/');
								}
							})
							.catch((e) => {
								console.log('login.error', e);
								loading.value = false;
							});
					} else {
						ElMessage.error('验证码不正确');
						loading.value = false;
						getCaptchaImage();
					}
				} catch (error) {
					loading.value = false;
					getCaptchaImage();
				}
			}
		});
	}

	// 刷新验证码
	const refreshCode = () => {
		getCaptchaImage();
	};

	function initOem() {
		const globalStore = useGlobalStoreWithOut();

		const logoUrl = getFrontEndConfigItemByCodeAndItemCode(
			globalStore.frontEndConfig,
			'login',
			'headerLogo',
		)?.itemValue;
		const backgroundImageUrl = getFrontEndConfigItemByCodeAndItemCode(
			globalStore.frontEndConfig,
			'login',
			'backgroundImage',
		)?.itemValue;

		if (backgroundImageUrl) {
			oemDetail.value.loginBackgroundImageUrl = `url(${getFileUrlById(backgroundImageUrl)})`;
		}

		if (logoUrl) {
			oemDetail.value.loginLogoUrl = getFileUrlById(logoUrl);
		}
	}

	// async function isExpiredReminder() {
	// 	const res: any = await grtIsExpiredReminder({});
	// 	if (res.code == 200) {
	// 		isExpired.value = res.data.expiredReminder;
	// 	}
	// }

	onMounted(() => {
		initOem();
		nextTick(() => {
			getCaptchaImage();
		});
		// isExpiredReminder();
	});

	onUnmounted(() => {
		if (showCodeTimer.value) {
			clearTimeout(showCodeTimer.value);
		}
	});
</script>

<style lang="scss" scoped>
	.login {
		--login-font-color: #f0f5ff;
		--login-border-color: rgba(255, 255, 255, 0.3);
		--login-placeholder-color: rgba(255, 255, 255, 0.5);
		height: 100vh;

		.empower {
			height: 120px;
			width: 500px;
			border: 1px solid #199be3;
			text-align: center;
			line-height: 120px;
			background-color: #f0f5ff;
			border-radius: 5px;

			span {
				display: inline-block;
				vertical-align: middle;
			}
		}

		.login-form {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			min-width: 400px;

			.logo {
				display: flex;
				align-items: center;
				margin-bottom: 50px;
				justify-content: space-around;

				.loginLogo {
					height: 54px;
				}

				.logo-divider {
					width: 1px;
					height: 44px;
					background: linear-gradient(to bottom, rgba(11, 53, 110, 0), rgba(255, 255, 255, 0.98), rgba(11, 53, 110, 0));
					opacity: 0.3;
					margin: 0 16px;
				}

				.loginName {
					font-size: 36px;
					color: var(--login-font-color);
					font-weight: 600;
				}
			}

			.el-form-item {
				margin-top: 24px;
				border-bottom: 1px solid var(--login-border-color);
			}

			.loginButton {
				margin-top: 12px;
			}

			.form-code {
				display: flex;
				align-items: center;

				.code-main {
					display: flex;
					justify-content: space-between;
					width: 100%;
					align-items: center;

					.code-image {
						height: 60px;
						width: 200px;
						cursor: pointer;

						.code-canvas {
							width: 100%;
							height: 100%;
						}
					}
				}
			}

			:deep(.el-form-item__label) {
				color: var(--login-font-color);
				opacity: 0.9;
				width: 70px;
				text-align-last: justify;
				display: inline-block;
				height: 100%;
				line-height: 40px;
			}

			:deep(.el-input__wrapper) {
				--el-input-placeholder-color: var(--login-placeholder-color);
				background: transparent;
				border: none;
				box-shadow: none;

				.el-input__inner {
					color: var(--login-font-color);
				}

				// 解决输入框自动填充时背景色变化
				input:-internal-autofill-previewed,
				input:-internal-autofill-selected {
					-webkit-text-fill-color: #ffffff !important;
					transition: background-color 5000s ease-in-out 0s !important;
				}
			}

			:deep(.el-input__icon) {
				color: var(--login-font-color);
			}
		}

		.el-login-footer {
			position: absolute;
			bottom: 20px;
			width: 100%;
			text-align: center;
			color: var(--login-font-color);
			opacity: 0.3;
			font-size: 14px;
			font-weight: 500;
			font-family: PingFang SC;
		}
	}
</style>
